'use client';

import { useNotifications } from '@/contexts/NotificationContext';
import Icon from '@/components/ui/AppIcon';
import { useRouter } from 'next/navigation';

const TYPE_STYLES = {
  success: {
    bg: 'bg-emerald-600',
    icon: 'CheckCircleIcon' as const,
    bar: 'bg-emerald-400',
  },
  error: {
    bg: 'bg-red-600',
    icon: 'XCircleIcon' as const,
    bar: 'bg-red-400',
  },
  warning: {
    bg: 'bg-amber-500',
    icon: 'ExclamationTriangleIcon' as const,
    bar: 'bg-amber-300',
  },
  info: {
    bg: 'bg-blue-600',
    icon: 'InformationCircleIcon' as const,
    bar: 'bg-blue-400',
  },
};

export default function ToastContainer() {
  const { toasts, dismissToast } = useNotifications();
  const router = useRouter();

  if (toasts.length === 0) return null;

  return (
    <div
      className="fixed bottom-6 right-6 z-[500] flex flex-col gap-3 max-w-sm w-full pointer-events-none"
      aria-live="polite"
      aria-label="Notifications"
    >
      {toasts.map(toast => {
        const style = TYPE_STYLES[toast.type];
        return (
          <div
            key={toast.id}
            className={`pointer-events-auto flex items-start gap-3 px-4 py-3 rounded-xl shadow-2xl text-white ${style.bg} animate-slide-up`}
            role="alert"
          >
            <Icon name={style.icon} size={18} variant="solid" className="flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="font-body text-sm font-semibold leading-tight">{toast.title}</p>
              <p className="font-caption text-xs opacity-90 mt-0.5 leading-snug">{toast.message}</p>
              {toast.actionUrl && toast.actionLabel && (
                <button
                  onClick={() => {
                    dismissToast(toast.id);
                    router.push(toast.actionUrl!);
                  }}
                  className="mt-1.5 text-xs font-semibold underline underline-offset-2 opacity-90 hover:opacity-100 transition-opacity"
                >
                  {toast.actionLabel} →
                </button>
              )}
            </div>
            <button
              onClick={() => dismissToast(toast.id)}
              className="flex-shrink-0 p-0.5 rounded opacity-70 hover:opacity-100 transition-opacity"
              aria-label="Dismiss notification"
            >
              <Icon name="XMarkIcon" size={14} />
            </button>
          </div>
        );
      })}
    </div>
  );
}
